//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import React from "react";
import { connect } from "react-redux";
import { QueryRenderer, ReadyState } from "react-relay";
// team-health-summary-renderer imports
import { IRendererProps, IRendererContainer } from "./CalendarProfile.d";
import { calendarProfileQuery } from "./CalendarProfileRenderer.gql";
import * as s from "./CalendarProfile.styles";
// other internals
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import { CalendarProfile } from "./CalendarProfile";
import { IReduxState } from "@reducers/root.reducer";
// util
import { getFetchInfo, getCalViewStateFromDateString } from "@util/team-health.util";
import { ZonedMoment } from "@util/time-zone.util";
// material-ui
import { isOnWeekInReviewOnProfile } from "@util/experiment.util";

//////////////////
// COMPONENT
//////////////////
class CalendarProfileRendererCmpt extends React.PureComponent<IRendererProps> {
  private cachedWeek?: string;
  // not on state b/c we don't want this to trigger a re-render
  private cachedOrg?: ISchema.IOrg;
  private cachedCalendarId?: string;

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-
  public render() {
    const { org, calendarId, viewWeek, chromeCalendarView, environment } = this.props;
    const showWeekInReview = !!isOnWeekInReviewOnProfile(org);
    const profileViewWeek = viewWeek ? getCalViewStateFromDateString(viewWeek) : undefined;
    const { fetchVariables } = getFetchInfo(
      org.id,
      !!profileViewWeek ? profileViewWeek : chromeCalendarView,
    );
    const rewindWeek =
      fetchVariables.rangeMin.date ||
      viewWeek ||
      new ZonedMoment().startOf("week").format("YYYY-MM-DD");

    const variables = {
      ...fetchVariables,
      showWeekInReview,
      calendarId,
      calendarIds: [calendarId],
      week: rewindWeek,
    };

    return (
      <QueryRenderer
        environment={environment}
        query={calendarProfileQuery}
        variables={variables}
        cacheConfig={{}}
        render={(readyState: ReadyState) => {
          const freshOrg = readyState.props && readyState.props.node;
          const showLoader = (!this.cachedOrg && !freshOrg) || calendarId !== this.cachedCalendarId;

          // check for error or show a loader for first load
          if (readyState.error) {
            return (
              <CalendarProfile
                org={freshOrg || this.cachedOrg}
                calendarId={calendarId}
                initialFetchError={readyState.error}
                showWeekInReview={showWeekInReview}
                rewindWeek={rewindWeek}
                stale
              />
            );
          }

          // loading for the first time
          if (showLoader) {
            // reset cache
            this.cachedCalendarId = calendarId;
            this.cachedOrg = undefined;

            return (
              <div style={s.styles.container}>
                <div style={s.styles.loading}>
                  <CircularProgress />
                </div>
              </div>
            );
          }

          // cache an org so it doesn't flash on new loads
          if (freshOrg) {
            this.cachedOrg = freshOrg;
            this.cachedWeek = rewindWeek;
          }

          return (
            <CalendarProfile
              key={this.cachedWeek!}
              org={this.cachedOrg!}
              calendarId={calendarId}
              showWeekInReview={showWeekInReview}
              rewindWeek={this.cachedWeek!}
            />
          );
        }}
      />
    );
  }
}

//////////////////
// REDUX
//////////////////
export const CalendarProfileRenderer = connect(
  (state: IReduxState, _ownProps: IRendererContainer) => ({
    calendarId: state.webExtension.activeViewCalendarProfile.calendarId,
    viewWeek: state.webExtension.activeViewCalendarProfile.viewWeek,
    chromeCalendarView: state.webExtension.calendarView,
  }),
  {},
)(CalendarProfileRendererCmpt);
